import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
    CButton,
    CCol,
    CImage,
    CForm,
    CFormInput,
    CInputGroup,
    CInputGroupText,
    CRow,
    CAlert,
    CFormFeedback,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilLockLocked, cilUser, cilEnvelopeClosed } from "@coreui/icons";
import { useNavigate } from "react-router-dom";
import bgimage from "../../assets/Images/logo_black.png";
import openEyeIcon from "../../assets/Images/eye-open.png";
import closedEyeIcon from "../../assets/Images/eye-closed.png";
import * as actions from "../../store/actions/index";
import { SOCKET_BASE_URL } from "../Helpers/helpers";

// ─── OTP / Registration constants ────────────────────────────────────────────
const OTP_LENGTH = 6;
const OTP_EXPIRY_SECONDS = 5 * 60; // 5 minutes
const RESEND_COOLDOWN_SECONDS = 30;
const MAX_OTP_ATTEMPTS = 5;

// Registration steps
const STEP_FORM = "FORM";
const STEP_OTP = "OTP";
const STEP_DONE = "DONE";

// ─── API helpers ─────────────────────────────────────────────────────────────
const apiRequest = async (path, body) => {
    let response;
    try {
        response = await fetch(`${SOCKET_BASE_URL}${path}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    } catch (networkErr) {
        throw new ApiError(
            "Network error. Please check your connection and try again.",
            0,
            networkErr,
        );
    }

    let data = null;
    try {
        data = await response.json();
    } catch {
    }

    if (!response.ok) {
        const message =
            data?.message ||
            data?.error ||
            defaultMessageForStatus(response.status);
        throw new ApiError(message, response.status, data);
    }

    return data;
};

class ApiError extends Error {
    constructor(message, status, payload) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.payload = payload;
    }
}

const defaultMessageForStatus = (status) => {
    switch (status) {
        case 400:
            return "Invalid request. Please check the details you entered.";
        case 401:
            return "Session expired. Please try again.";
        case 409:
            return "An account with this email already exists.";
        case 422:
            return "The information provided is invalid.";
        case 429:
            return "Too many attempts. Please wait before trying again.";
        case 500:
        case 502:
        case 503:
            return "Server error. Please try again in a moment.";
        default:
            return "Something went wrong. Please try again.";
    }
};

const registerApi = {
  register: (payload) =>
    apiRequest("/api/v1/user/register", payload),

  verifyOtp: (payload) =>
    apiRequest(
      "/api/v1/user/auth/verify-email-otp",
      payload
    ),

  resendOtp: (payload) =>
    apiRequest(
      "/api/v1/user/auth/resend-email-otp",
      payload
    ),
};

// ─── Validation helpers ──────────────────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateForm = ({ firstName, lastName, email, password, confirmPassword }) => {
    const errors = {};
    if (!firstName || firstName.trim().length < 2) {
        errors.firstName = "Please enter your first name.";
    }
    if (!lastName || lastName.trim().length < 2) {
        errors.lastName = "Please enter your last name.";
    }
    if (!email || !EMAIL_RE.test(email)) {
        errors.email = "Please enter a valid email address.";
    }
    if (!password || password.length < 8) {
        errors.password = "Password must be at least 8 characters.";
    }
    if (confirmPassword !== password) {
        errors.confirmPassword = "Passwords do not match.";
    }
    return errors;
};

// ─── Component ────────────────────────────────────────────────────────────────
const RegisterNew = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [step, setStep] = useState(STEP_FORM);

    // Form fields
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);

    const [fieldErrors, setFieldErrors] = useState({});
    const [validated, setValidated] = useState(false);

    // Alert / feedback state
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    const [loading, setLoading] = useState(false);

    // OTP state (sent to email)
    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
    const [otpExpiresAt, setOtpExpiresAt] = useState(null);
    const [otpRemainingSeconds, setOtpRemainingSeconds] = useState(0);
    const [otpAttemptsLeft, setOtpAttemptsLeft] = useState(MAX_OTP_ATTEMPTS);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendLoading, setResendLoading] = useState(false);
    const otpInputRefs = useRef([]);

    // ── OTP expiry countdown ──────────────────────────────────────────────────
    useEffect(() => {
        if (step !== STEP_OTP || !otpExpiresAt) return;

        const tick = () => {
            const secs = Math.max(0, Math.ceil((otpExpiresAt - Date.now()) / 1000));
            setOtpRemainingSeconds(secs);
            if (secs <= 0) {
                setShowError(true);
                setErrorMessage("Your OTP has expired. Please request a new code.");
            }
        };

        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [step, otpExpiresAt]);

    // ── Resend cooldown countdown ─────────────────────────────────────────────
    useEffect(() => {
        if (resendCooldown <= 0) return;
        const interval = setInterval(() => {
            setResendCooldown((s) => Math.max(0, s - 1));
        }, 1000);
        return () => clearInterval(interval);
    }, [resendCooldown]);

    const formatCountdown = (secs) => {
        const m = Math.floor(secs / 60).toString().padStart(2, "0");
        const s = (secs % 60).toString().padStart(2, "0");
        return `${m}:${s}`;
    };

    // ── Field change handlers ─────────────────────────────────────────────────
    const clearFieldError = (field) => {
        setFieldErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
        setShowError(false);
    };

    const handleFirstNameChange = (e) => {
        setFirstName(e.target.value);
        clearFieldError("firstName");
    };
    const handleLastNameChange = (e) => {
        setLastName(e.target.value);
        clearFieldError("lastName");
    };
    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        clearFieldError("email");
    };
    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        clearFieldError("password");
    };
    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
        clearFieldError("confirmPassword");
    };

    // ── Submit registration form ──────────────────────────────────────────────
    const handleRegisterSubmit = async (event) => {
        event.preventDefault();

        const errors = validateForm({
            firstName,
            lastName,
            email,
            password,
            confirmPassword,
        });

        setFieldErrors(errors);
        setValidated(true);

        if (Object.keys(errors).length > 0) {
            return;
        }

        setLoading(true);
        setShowError(false);
        setErrorMessage("");

        try {
            const payload = {
                FirstName: firstName.trim(),
                LastName: lastName.trim(),
                EmailAddress: email.trim().toLowerCase(),
                Password: password,
                ConfirmPassword: confirmPassword,
            };

            const response = await registerApi.register(payload);

            if (response && response.success === true) {
                const registeredEmail = payload.EmailAddress;

                navigate(
                    `/verify-email?email=${encodeURIComponent(registeredEmail)}`,
                    { replace: true },
                );

                setStep(STEP_OTP);
                setOtpExpiresAt(Date.now() + OTP_EXPIRY_SECONDS * 1000);
                return;
            }

            setShowError(true);
            setErrorMessage(
                response?.message || "Registration failed. Please try again."
            );
        } catch (err) {
            setShowError(true);

            if (err instanceof ApiError) {
                if (err.status === 409) {
                    setFieldErrors({ email: "This email is already registered." });
                }
                setErrorMessage(err.message);
            } else {
                setErrorMessage("An unexpected error occurred. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    // ── OTP input handlers ────────────────────────────────────────────────────
    const handleOtpChange = (index, value) => {
        const digit = value.replace(/[^0-9]/g, "").slice(-1);
        const next = [...otp];
        next[index] = digit;
        setOtp(next);
        setShowError(false);

        if (digit && index < OTP_LENGTH - 1) {
            otpInputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            otpInputRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
        if (!pasted) return;
        e.preventDefault();
        const next = Array(OTP_LENGTH).fill("");
        pasted
            .slice(0, OTP_LENGTH)
            .split("")
            .forEach((d, i) => (next[i] = d));
        setOtp(next);
        const lastIndex = Math.min(pasted.length, OTP_LENGTH) - 1;
        otpInputRefs.current[lastIndex]?.focus();
    };

    // ── Verify OTP (email code) ──────────────────────────────────────────────
    const handleVerifyOtp = async (event) => {
        event.preventDefault();

        if (otpRemainingSeconds <= 0) {
            setShowError(true);
            setErrorMessage("Your OTP has expired. Please request a new code.");
            return;
        }

        const code = otp.join("");
        if (code.length !== OTP_LENGTH) {
            setShowError(true);
            setErrorMessage(`Please enter the ${OTP_LENGTH}-digit code.`);
            return;
        }

        setLoading(true);
        setShowError(false);

        try {
            await registerApi.verifyOtp({
                email: email.trim().toLowerCase(),
                code,
            });

            try {
                await dispatch(actions.userLogin(email.trim().toLowerCase(), password));
            } catch {
                // Non-fatal: verification succeeded even if auto-login didn't.
            }

            setStep(STEP_DONE);
            setShowSuccess(true);
            setSuccessMessage("Your account has been verified successfully!");
            setTimeout(() => navigate("/query"), 1200);
        } catch (err) {
            setShowError(true);
            if (err instanceof ApiError) {
                if (err.status === 410) {
                    setOtpRemainingSeconds(0);
                    setErrorMessage("Your OTP has expired. Please request a new code.");
                } else {
                    const attemptsLeft = Math.max(0, otpAttemptsLeft - 1);
                    setOtpAttemptsLeft(attemptsLeft);
                    setErrorMessage(
                        attemptsLeft > 0
                            ? `${err.message} (${attemptsLeft} attempt${attemptsLeft !== 1 ? "s" : ""} left)`
                            : "Too many incorrect attempts. Please request a new code.",
                    );
                }
            } else {
                setErrorMessage("An unexpected error occurred. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    // ── Resend OTP (email code) ──────────────────────────────────────────────
    const handleResendOtp = async () => {
        if (resendCooldown > 0 || resendLoading) return;

        setResendLoading(true);
        setShowError(false);

        try {
            const data = await registerApi.resendOtp({
                email: email.trim().toLowerCase(),
            });

            const expiresInSeconds = data?.expiresIn ?? OTP_EXPIRY_SECONDS;
            setOtpExpiresAt(Date.now() + expiresInSeconds * 1000);
            setOtpAttemptsLeft(MAX_OTP_ATTEMPTS);
            setOtp(Array(OTP_LENGTH).fill(""));
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
            setShowSuccess(true);
            setSuccessMessage("A new verification code has been emailed to you.");
            otpInputRefs.current[0]?.focus();
        } catch (err) {
            setShowError(true);
            setErrorMessage(
                err instanceof ApiError
                    ? err.message
                    : "Could not resend the code. Please try again.",
            );
        } finally {
            setResendLoading(false);
        }
    };

    const goBackToForm = () => {
        setStep(STEP_FORM);
        setShowError(false);
        setShowSuccess(false);
    };

    const eyeIcon = (visible) => (visible ? openEyeIcon : closedEyeIcon);
    const errorPosition = { position: "absolute", lineHeight: "10px" };

    return (
        <div className="bg-light min-vh-100 d-flex flex-row align-items-center">
            <div className="lgnauth">
                <div className="lgnform">
                    <CRow className="justify-content-center">
                        <CCol md={12}>
                            {step === STEP_FORM && (
                                <CForm
                                    className="row g-3 needs-validation"
                                    noValidate
                                    validated={validated}
                                    onSubmit={handleRegisterSubmit}
                                >
                                    <h2 className="lgntxt">Create Account</h2>

                                    {showError && (
                                        <CAlert
                                            color="warning"
                                            dismissible
                                            onClose={() => setShowError(false)}
                                        >
                                            {errorMessage}
                                        </CAlert>
                                    )}
                                    {showSuccess && (
                                        <CAlert
                                            color="success"
                                            dismissible
                                            onClose={() => setShowSuccess(false)}
                                        >
                                            {successMessage}
                                        </CAlert>
                                    )}

                                    {/* First / Last name — two columns in one row */}
                                    <CRow className="g-2 mb-2">
                                        <CCol xs={6}>
                                            <CInputGroup>
                                                <div className="single-input-field">
                                                    <CInputGroupText>
                                                        <CIcon icon={cilUser} />
                                                    </CInputGroupText>
                                                    <CFormInput
                                                        id="FirstName"
                                                        type="text"
                                                        placeholder="First name"
                                                        value={firstName}
                                                        onChange={handleFirstNameChange}
                                                        required
                                                        invalid={!!fieldErrors.firstName}
                                                    />
                                                    <CFormFeedback invalid style={errorPosition}>
                                                        {fieldErrors.firstName || "Please enter your first name."}
                                                    </CFormFeedback>
                                                </div>
                                            </CInputGroup>
                                        </CCol>
                                        <CCol xs={6}>
                                            <CInputGroup>
                                                <div className="single-input-field">
                                                    <CInputGroupText>
                                                        <CIcon icon={cilUser} />
                                                    </CInputGroupText>
                                                    <CFormInput
                                                        id="LastName"
                                                        type="text"
                                                        placeholder="Last name"
                                                        value={lastName}
                                                        onChange={handleLastNameChange}
                                                        required
                                                        invalid={!!fieldErrors.lastName}
                                                    />
                                                    <CFormFeedback invalid style={errorPosition}>
                                                        {fieldErrors.lastName || "Please enter your last name."}
                                                    </CFormFeedback>
                                                </div>
                                            </CInputGroup>
                                        </CCol>
                                    </CRow>

                                    {/* Email */}
                                    <CInputGroup className="mb-2">
                                        <div className="single-input-field">
                                            <CInputGroupText>
                                                <CIcon icon={cilEnvelopeClosed} />
                                            </CInputGroupText>
                                            <CFormInput
                                                id="EmailAddress"
                                                type="email"
                                                placeholder="Enter email"
                                                value={email}
                                                onChange={handleEmailChange}
                                                required
                                                invalid={!!fieldErrors.email}
                                            />
                                            <CFormFeedback invalid style={errorPosition}>
                                                {fieldErrors.email || "Please enter a valid email address."}
                                            </CFormFeedback>
                                        </div>
                                    </CInputGroup>

                                    {/* Password */}
                                    <CInputGroup className="mb-2">
                                        <div className="single-input-field">
                                            <CInputGroupText>
                                                <CIcon icon={cilLockLocked} />
                                            </CInputGroupText>
                                            <CFormInput
                                                id="Password"
                                                type={passwordVisible ? "text" : "password"}
                                                placeholder="Enter password"
                                                value={password}
                                                onChange={handlePasswordChange}
                                                required
                                                invalid={!!fieldErrors.password}
                                            />
                                            <CInputGroupText
                                                className="imgpass-2"
                                                onClick={() => setPasswordVisible((v) => !v)}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <CImage
                                                    src={eyeIcon(passwordVisible)}
                                                    className="eyeimge"
                                                    style={{ width: "20px" }}
                                                />
                                            </CInputGroupText>
                                            <CFormFeedback invalid style={errorPosition}>
                                                {fieldErrors.password ||
                                                    "Password must be at least 8 characters."}
                                            </CFormFeedback>
                                        </div>
                                    </CInputGroup>

                                    {/* Confirm password */}
                                    <CInputGroup className="mb-3">
                                        <div className="single-input-field">
                                            <CInputGroupText>
                                                <CIcon icon={cilLockLocked} />
                                            </CInputGroupText>
                                            <CFormInput
                                                id="ConfirmPassword"
                                                type={confirmVisible ? "text" : "password"}
                                                placeholder="Confirm password"
                                                value={confirmPassword}
                                                onChange={handleConfirmPasswordChange}
                                                required
                                                invalid={!!fieldErrors.confirmPassword}
                                            />
                                            <CInputGroupText
                                                className="imgpass-2"
                                                onClick={() => setConfirmVisible((v) => !v)}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <CImage
                                                    src={eyeIcon(confirmVisible)}
                                                    className="eyeimge"
                                                    style={{ width: "20px" }}
                                                />
                                            </CInputGroupText>
                                            <CFormFeedback invalid style={errorPosition}>
                                                {fieldErrors.confirmPassword || "Passwords do not match."}
                                            </CFormFeedback>
                                        </div>
                                    </CInputGroup>

                                    <CButton
                                        color="primary"
                                        className="px-4 btnsign"
                                        type="submit"
                                        disabled={loading}
                                    >
                                        {loading ? "Please wait..." : "Create Account"}
                                    </CButton>

                                    <div className="text-center mt-2">
                                        <span style={{ fontSize: "14px", color: "#1e2433" }}>
                                            Already have an account?{" "}
                                            <span
                                                style={{
                                                    color: "#1e2433",
                                                    fontWeight: 600,
                                                    cursor: "pointer",
                                                    textDecoration: "underline",
                                                }}
                                                onClick={() => navigate("/login")}
                                            >
                                                Login Now
                                            </span>
                                        </span>
                                    </div>
                                </CForm>
                            )}

                            {step === STEP_OTP && (
                                <CForm
                                    className="row g-3 needs-validation"
                                    noValidate
                                    onSubmit={handleVerifyOtp}
                                >
                                    <h2 className="lgntxt">Verify Your Email</h2>
                                    <p style={{ fontSize: "14px", color: "#5b6472" }}>
                                        Enter the {OTP_LENGTH}-digit code emailed to{" "}
                                        <strong>{email}</strong>
                                    </p>

                                    {showError && (
                                        <CAlert
                                            color="warning"
                                            dismissible
                                            onClose={() => setShowError(false)}
                                        >
                                            {errorMessage}
                                        </CAlert>
                                    )}
                                    {showSuccess && (
                                        <CAlert
                                            color="success"
                                            dismissible
                                            onClose={() => setShowSuccess(false)}
                                        >
                                            {successMessage}
                                        </CAlert>
                                    )}

                                    {/* OTP boxes — same input-field styling as the form step */}
                                    <CInputGroup className="mb-2">
                                        <div
                                            className="single-input-field"
                                            style={{
                                                display: "flex",
                                                gap: 8,
                                                justifyContent: "center",
                                                width: "100%",
                                                padding: "8px 0",
                                            }}
                                            onPaste={handleOtpPaste}
                                        >
                                            {otp.map((digit, i) => (
                                                <input
                                                    key={i}
                                                    ref={(el) => (otpInputRefs.current[i] = el)}
                                                    value={digit}
                                                    onChange={(e) => handleOtpChange(i, e.target.value)}
                                                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                                                    inputMode="numeric"
                                                    maxLength={1}
                                                    disabled={otpRemainingSeconds <= 0}
                                                    style={{
                                                        width: 44,
                                                        height: 52,
                                                        textAlign: "center",
                                                        fontSize: "1.3em",
                                                        fontWeight: 600,
                                                        borderRadius: 8,
                                                        border: "1px solid #ccc",
                                                        outline: "none",
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </CInputGroup>

                                    {/* Expiry countdown */}
                                    <div className="text-center" style={{ fontSize: "14px" }}>
                                        {otpRemainingSeconds > 0 ? (
                                            <span>
                                                Code expires in{" "}
                                                <strong>{formatCountdown(otpRemainingSeconds)}</strong>
                                            </span>
                                        ) : (
                                            <span style={{ color: "#e55353", fontWeight: 600 }}>
                                                Code expired
                                            </span>
                                        )}
                                    </div>

                                    <CButton
                                        color="primary"
                                        className="px-4 btnsign"
                                        type="submit"
                                        disabled={loading || otpRemainingSeconds <= 0}
                                    >
                                        {loading ? "Verifying..." : "Verify"}
                                    </CButton>

                                    <div className="text-center" style={{ fontSize: "14px" }}>
                                        Didn't get the code?{" "}
                                        {resendCooldown > 0 ? (
                                            <span style={{ color: "#8a92a3" }}>
                                                Resend in {formatCountdown(resendCooldown)}
                                            </span>
                                        ) : (
                                            <span
                                                style={{
                                                    color: "#1e2433",
                                                    fontWeight: 600,
                                                    cursor: resendLoading ? "not-allowed" : "pointer",
                                                    textDecoration: "underline",
                                                }}
                                                onClick={handleResendOtp}
                                            >
                                                {resendLoading ? "Sending..." : "Resend code"}
                                            </span>
                                        )}
                                    </div>

                                    <div className="text-center mt-2">
                                        <span style={{ fontSize: "14px", color: "#1e2433" }}>
                                            Wrong details?{" "}
                                            <span
                                                style={{
                                                    color: "#1e2433",
                                                    fontWeight: 600,
                                                    cursor: "pointer",
                                                    textDecoration: "underline",
                                                }}
                                                onClick={goBackToForm}
                                            >
                                                Back to details
                                            </span>
                                        </span>
                                    </div>
                                </CForm>
                            )}

                            {step === STEP_DONE && (
                                <div className="text-center">
                                    <h2 className="lgntxt">You're all set!</h2>
                                    <CAlert color="success">{successMessage}</CAlert>
                                    <p style={{ fontSize: "14px" }}>Redirecting…</p>
                                </div>
                            )}
                        </CCol>
                    </CRow>
                </div>

                {/* ── Logo / welcome panel (shared across all steps) ── */}
                <div className="logobg">
                    <h2 className="weltxt">Welcome To</h2>
                    <CImage
                        src={bgimage}
                        height="auto"
                        align="center"
                        width={300}
                        className="mainlgo"
                    />
                    <p className="qqtxt">
                        Lorem Ipsum is simply dummy text of the printing and typesetting
                        industry. Lorem Ipsum has been the industry's standard dummy text
                        ever since the 1500s, when an unknown printer took a galley of type
                        and scrambled it to make a type specimen book.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default RegisterNew;